<script setup lang="ts">
import { useLyricsTiming } from '~/composables/useLyricsTiming';
import { songRequest } from '~/services/songPlayback';
import { createGrowthEventId } from '~/utils/growthEvents';
import type { PlayerTrack } from '~/stores/player';
import CreatorCredit from '~/components/mm/music/CreatorCredit.vue';
import SimilarSongs from '~/components/mm/music/SimilarSongs.vue';
import { useShareIntent } from '~/components/mm/creator/incentives/useShareIntent';
import { useI18n } from 'vue-i18n';
import { localeMappings } from '~/i18n/config';
import { useRoute, useRouter, ref, computed, nextTick, useNuxtApp, onMounted, onUnmounted, onActivated, onDeactivated, useSession, useHead, watch, useRuntimeConfig, useSeoMeta, setResponseStatus, useResponseHeader } from '#imports';
import { useAuthState } from '~/composables/useAuthState';
import { useToast } from '~/composables/useToast';
import { musicService } from '~/services/musicService';
import { useMusicCreate } from '~/composables/useMusicCreate';
import { seoLandingPages } from '~/services/seoLandingConfig';
import type { SeoLandingPage } from '~/services/seoLandingConfig';
import { trackEvent } from '~/utils/analytics';
import { usePaywall } from '~/composables/usePaywall';
import { pricingPath } from '~/utils/checkout-source.mjs';
import { usePageReadingPosition } from '~/composables/usePageReadingPosition';
import { musicCoverUrl, musicCoverSrcset } from '~/utils/music-cover';
import { usePlayerStore } from '~/stores/player';
import MmButton from '~/components/mm/base/MmButton.vue';
import MmIcon from '~/components/mm/base/MmIcon.vue';
import MmMenu from '~/components/mm/base/MmMenu.vue';
import GiftShareDialog from '~/components/mm/music/GiftShareDialog.vue';
import MmShareDialog from '~/components/mm/base/MmShareDialog.vue';
import MmSegmented from '~/components/mm/base/MmSegmented.vue';
import type { MmMenuItem } from '~/components/mm/base/menu';
import PlayButton from '~/components/mm/music/PlayButton.vue';
import Vinyl from '~/components/mm/music/Vinyl.vue';
import CoverArt from '~/components/mm/music/CoverArt.vue';
import LyricsSingAlong from '~/components/mm/music/LyricsSingAlong.vue';
import LyricsTakeover from '~/components/mm/music/LyricsTakeover.vue';
import { generateFullSlug } from '~/utils/string';
import { songRawTags, songTagLabels, songSeoTagText } from '~/components/mm/music/song-raw-tags.mjs';

const { user, userAccount, handleSignIn, refreshUserAccount, isAuthReady, isAuthenticated, fetchAndStoreToken } = useAuthState();
const { openPaywall } = usePaywall();
const { session } = useSession();

const toast = useToast();
const siteUrl = useRuntimeConfig().public.siteUrl || 'https://musicmint.ai';
const { saveMusicData } = useMusicCreate();

// 定义统一的音乐视图模型
interface MusicViewModel extends PlayerTrack {
  id: string;
  title: string;
  image_url: string;
  audio_url: string;
  display_name: string;
  releaseDate: string;
  duration: number;
  tags: string;
  hasStyleTags: boolean;
  description: string;
  cover_hue: number;
  ownerId: string;
  playCount?: number;
  likeCount?: number;
  linerPrompt: string;
  mood: string;
  voice: string;
}

type PublicMusicDetailStatus = 'available' | 'gone' | 'notFound' | 'unavailable' | 'skipped';
const SONG_SSR_TIMEOUT_MS = 2500;

interface PublicMusicDetailResult {
  status: PublicMusicDetailStatus;
  data: any | null;
}

const route = useRoute();
const router = useRouter();
const musicId = computed(() => route.params.id);
const taskId = computed(() => route.query.taskId as string || '');
const { t, locale } = useI18n();
const shareIntent = useShareIntent();

// 状态
const isLoading = ref(true);
const musicLoadError = ref('');
const readingPath = route.path;
const isPrivateSong = computed(() => !!taskId.value);
const songTargetKey = computed(() => JSON.stringify([musicId.value?.toString() || '', taskId.value]));
const songSessionIdentity = computed(() => JSON.stringify([
  isAuthReady.value, isAuthenticated.value, user.value?.id || '', session.value?.id || '',
]));
const canReadPrivateSong = computed(() => isAuthReady.value && isAuthenticated.value && !!user.value?.id && !!session.value?.id);
const needsSongSignIn = computed(() => isPrivateSong.value && isAuthReady.value && !isAuthenticated.value);
const songPageActive = ref(false);
let songHasMounted = false;
const { pageRoot: songRoot, pageHeading: songHeading, restoreAfterLoad } = usePageReadingPosition(() => readingPath, { ready: () => !isLoading.value });
watch(isLoading, loading => { if (!loading) void restoreAfterLoad(); });
// The feed's explicit Lyrics destination takes precedence over page-position
// restoration. Wait until Nuxt has finished attaching the destination page.
let lyricsFrame = 0;
const revealLyrics = async () => {
  if (!import.meta.client || route.path !== readingPath || route.hash !== '#lyrics' || isLoading.value) return;
  await nextTick();
  cancelAnimationFrame(lyricsFrame);
  lyricsFrame = requestAnimationFrame(() => {
    if (route.path !== readingPath || route.hash !== '#lyrics') return;
    const anchor = songRoot.value?.querySelector<HTMLElement>('#lyrics');
    anchor?.scrollIntoView({ block: 'start', behavior: 'instant' });
    anchor?.focus({ preventScroll: true });
  });
};
const stopLyricsNavigation = useNuxtApp().hook('page:loading:end', revealLyrics);
onMounted(revealLyrics);
watch([isLoading, () => route.hash], revealLyrics, { flush: 'post' });
onUnmounted(() => { stopLyricsNavigation(); cancelAnimationFrame(lyricsFrame); });
let musicDataRequest = 0;
let musicDataController: AbortController | undefined;
let loadedPrivateIdentity = '';
let loadedSongTarget = '';
const music = ref<MusicViewModel | null>(null);
const taskVersions = ref<Array<{ id: string; duration: number; position: number; image_url?: string }>>([]);
const sideOptions = computed(() => taskVersions.value.map(version => ({
  value: version.id,
  to: router.resolve({ path: '/music/song/' + version.id, query: { taskId: taskId.value } }).href,
  replace: true,
  label: t('mm.song.side', { side: String.fromCharCode(65 + version.position) }),
  ariaLabel: (music.value?.title || '') + ' · ' + t('mm.song.side', { side: String.fromCharCode(65 + version.position) }) + ' · ' + formatDuration(version.duration),
})));
const currentVersion = computed(() => taskVersions.value.find(version => version.id === music.value?.id));
const songStyleTags = computed(() => {
  if (!music.value?.hasStyleTags) return [];
  return [...new Set(songTagLabels(music.value.tags))].slice(0, 4);
});
const loadedTaskOwnerId = ref('');
const taskHasDisplayName = ref(false);
const coverError = ref(false);
const desktopCoverImage = ref<HTMLImageElement | null>(null);
watch(() => music.value?.image_url, () => { coverError.value = false; });
const coverSrc = computed(() => coverError.value ? music.value?.image_url || '' : musicCoverUrl(music.value?.image_url || '', 400));
const coverSrcset = computed(() => coverError.value ? undefined : musicCoverSrcset(music.value?.image_url || '', [160, 200, 320, 400, 600]));
const coverSizes = '(min-width: 1280px) 380px, (min-width: 900px) 340px, 72vw';
useHead(() => music.value?.image_url ? {
  link: [{ key: 'song-cover', rel: 'preload', as: 'image', href: coverSrc.value, imagesrcset: coverSrcset.value, imagesizes: coverSizes, fetchpriority: 'high' }],
} : {});
const player = usePlayerStore();
const isCurrentTrack = computed(() => !!music.value && player.currentTrack?.id === music.value.id);
const isPlaying = computed(() => isCurrentTrack.value && player.playing);
const isAudioLoading = computed(() => isCurrentTrack.value && player.loading);
const playbackState = computed(() => isAudioLoading.value ? 'loading' : isPlaying.value ? 'playing' : 'idle');
const isOwner = computed(() => !!userAccount.value?.id && (loadedTaskOwnerId.value || music.value?.ownerId) === userAccount.value.id);
const lyricsMode = ref('sync');
const lyricsOpen = ref(false);
const menuOpen = ref(false);
const giftOpen = ref(false);
const giftConfig = useRuntimeConfig();
const canGift = computed(() => [true, 'true'].includes(giftConfig.public.giftShareEnabled) && isOwner.value);
const shareOpen = ref(false);
const shareUrl = ref('');
const shareBusy = ref(false);
const shareError = ref('');
let shareRequest = 0;
const pendingSeek = ref<{ id: string; seconds: number } | null>(null);
const pageRequestId = ref('');
const lyricsLineCount = computed(() => lyricsSections.value.reduce((count, section) => count + section.lines.length, 0));
const songDuration = computed(() => isCurrentTrack.value && player.duration > 0 ? player.duration : music.value?.duration || 0);
const timingSong = computed(() => music.value ? { ...music.value, ...(player.timingUpdates[music.value.id] || {}) } : null);
const { lines: timedLines, sections: lyricsSections, activeLine, estimated } = useLyricsTiming(timingSong,
  computed(() => isCurrentTrack.value ? player.currentTime : -1), songDuration);
const canRemix = computed(() => music.value?.remix_allowed !== false);
const likeBusy = ref(false);
const likeError = ref('');
const pendingLike = ref('');
const rememberPendingLike = (id = '') => {
  pendingLike.value = id;
  try {
    if (id) sessionStorage.setItem('musicmint_pending_song_like', JSON.stringify({ id, at: Date.now() }));
    else sessionStorage.removeItem('musicmint_pending_song_like');
  } catch { /* In-memory sign-in continuation still works without storage. */ }
};
const localLike = ref<{ liked: boolean; like_count: number } | null>(null);
const likeState = computed(() => localLike.value || (music.value && player.likeUpdates[music.value.id])
  || { liked: music.value?.liked_by_me === true, like_count: music.value?.likeCount || 0 });
const toggleLike = async (forceLike = false) => {
  if (!music.value || likeBusy.value) return;
  if (!isAuthenticated.value) {
    rememberPendingLike(music.value.id);
    await handleSignIn();
    return;
  }
  const id = music.value.id, identity = songSessionIdentity.value;
  const before = { ...likeState.value };
  const liked = forceLike || !before.liked;
  likeBusy.value = true; likeError.value = '';
  localLike.value = { liked, like_count: Math.max(0, before.like_count + (liked === before.liked ? 0 : liked ? 1 : -1)) };
  try {
    const token = await fetchAndStoreToken();
    if (identity !== songSessionIdentity.value || music.value?.id !== id || !songPageActive.value) return;
    if (!token) throw new Error('Sign in required');
    const data = await songRequest(liked ? 'music/v1/like' : 'music/v1/unlike', { id, surface: 'song_page' });
    if (identity !== songSessionIdentity.value || music.value?.id !== id || !songPageActive.value) return;
    if (typeof data?.liked !== 'boolean' || !Number.isFinite(data.like_count)) throw new Error('Invalid like response');
    player.likeUpdates[id] = { liked: data.liked, like_count: Math.max(0, data.like_count) };
    if (player.currentTrack?.id === id) Object.assign(player.currentTrack, { liked_by_me: data.liked, like_count: data.like_count });
    for (const track of player.queue) if (track.id === id) Object.assign(track, { liked_by_me: data.liked, like_count: data.like_count });
    localLike.value = null;
  } catch {
    if (identity === songSessionIdentity.value && music.value?.id === id) { localLike.value = before; likeError.value = t('mm.song.likeFailed'); }
  } finally { likeBusy.value = false; }
};
const syncSongLikeIdentity = async () => {
  localLike.value = null; player.likeUpdates = {}; likeError.value = '';
  if (!songPageActive.value || !music.value || !isAuthenticated.value) return;
  if (pendingLike.value === music.value.id) { rememberPendingLike(); await toggleLike(true); }
  else if (!isPrivateSong.value) {
    const id = music.value.id, identity = songSessionIdentity.value;
    try {
      const token = await fetchAndStoreToken();
      if (!token || identity !== songSessionIdentity.value) return;
      const data = await songRequest('music/v1/get', { id });
      if (identity === songSessionIdentity.value && music.value?.id === id) music.value = convertToViewModel(data);
    } catch { /* The public SSR song remains readable. */ }
  }
};
watch(songSessionIdentity, syncSongLikeIdentity);
watch(() => music.value?.id, () => { localLike.value = null; likeError.value = ''; pendingLike.value = ''; });
const similarTo = computed(() => ({ path: '/create', query: { similar: music.value?.id || '' } }));
const songMenuItems = computed<MmMenuItem[]>(() => [
  { id: 'next', label: t('mm.fidelity.playNext'), icon: 'play' },
  ...(canGift.value ? [{ id: 'gift', label: t('share.gift.send'), icon: 'gift' as const }] : []),
  { id: 'like', label: t('t24b.addLiked'), icon: 'heart', disabled: likeBusy.value },
  ...(canRemix.value ? [{ id: 'similar', label: t('t24b.createSimilar'), icon: 'wand' as const }] : []),
  { id: 'share', label: t('albumCoverGenerator.preview.share'), icon: 'share' },
  // The existing download handler keeps its owner, sign-in and VIP checks.
  { id: 'download', label: t('music.download') + ' ' + (music.value?.title || ''), icon: 'download', disabled: isDownloading.value },
  ...(isOwner.value && taskId.value ? [{ id: 'certificate', label: t('ux.certificate'), icon: 'award' as const, to: '/copyright?task_id=' + encodeURIComponent(taskId.value) + '&music_id=' + encodeURIComponent(music.value?.id || '') }] : []),
]);
const selectSongMenu = (item: Extract<MmMenuItem, { id: string }>) => {
  // Let MmMenu release its focus trap before opening another dialog.
  if (item.id === 'next' && music.value) {
    const queue = player.queue.filter(song => song.id !== music.value?.id);
    queue.splice(queue.findIndex(song => song.id === player.currentTrack?.id) + 1, 0, { ...music.value, lyrics: music.value.description });
    player.queue = queue;
  }
  else if (item.id === 'download' && music.value) void handleDownloadMusic(music.value);
  else if (item.id === 'like') { if (!likeState.value.liked) void toggleLike(); }
  else if (item.id === 'similar') handleCreateSimilar();
  else if (item.id === 'gift') nextTick(() => { giftOpen.value = true; });
  else if (item.id === 'share') nextTick(() => { void handleShare(); });
  else if (item.id === 'lyrics') nextTick(() => { lyricsOpen.value = true; });
};
const compactCount = (value: number) => new Intl.NumberFormat(locale.value, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
const isUnavailable = computed(() => !isLoading.value && !music.value && !musicLoadError.value);


const getPublicMusicDetailErrorStatus = (error: any): number | null => {
  const statuses = [error?.response?.status, error?.response?.data?.code, error?.code].map(Number);
  const status = statuses.find(status => status === 404 || status === 410) || statuses.find(Number.isFinite);
  return Number.isFinite(status) ? status : null;
};

// 从Music或GenMusic转换为统一的MusicViewModel
const convertToViewModel = (data: any): MusicViewModel => {
  // 根据字段判断是GenMusic还是Music
  const isGenMusic = 'audio_oss' in data || 'stream_audio_url' in data;

  return {
    ...data,
    id: data.id || '',
    title: data.title || '',
    image_url: data.image_url || '',
    audio_url: isGenMusic
      ? (data.audio_url || data.audio_oss || data.stream_audio_url || '')
      : (data.audio_url || ''),
    display_name: data.creator?.display_name || t('ux.creatorUnavailable'),
    releaseDate: data.created_at || data.releaseDate || '',
    duration: isGenMusic
      ? (data.metadata?.duration || 0)
      : (data.metadata?.duration || 0),
    tags: songRawTags(data.metadata?.tags),
    hasStyleTags: songTagLabels(data.metadata?.tags).length > 0,
    description: data.show_lyrics === false ? '' : data.metadata?.prompt || data.lyrics_text || '',
    cover_hue: typeof data.cover_hue === 'number' && Number.isFinite(data.cover_hue) ? ((data.cover_hue % 360) + 360) % 360 : 152,
    ownerId: data.user_id || '',
    playCount: typeof data.play_count === 'number' ? data.play_count : undefined,
    likeCount: typeof data.like_count === 'number' ? data.like_count : 0,
    linerPrompt: data.metadata?.gpt_description_prompt || '',
    mood: data.metadata?.moods || '',
    voice: data.metadata?.has_vocal === false && !data.metadata?.prompt?.trim() ? t('create.voice.instrumental.label') : (data.metadata?.tags || '').split(/[,，]/).map((tag: string) => tag.trim()).find((tag: string) => /vocal|duet|band/i.test(tag)) || ''
  };
};

// SSR 预取：公开歌曲（无 taskId）在服务端获取数据，确保爬虫能拿到正确 title
const { data: ssrMusicResult } = await useAsyncData<PublicMusicDetailResult>(
  `song-${route.params.id}`,
  async () => {
    // taskId 是用户私有任务数据，只在客户端获取
    if (route.query.taskId) return { status: 'skipped', data: null };
    const id = route.params.id as string;
    if (!id) return { status: 'notFound', data: null };
    try {
      const result = await musicService.getPublicMusicDetail(id, { timeout: SONG_SSR_TIMEOUT_MS });
      if (result?.code === 200 && result.data) {
        return { status: 'available', data: result.data };
      }
      if (result?.code === 410) return { status: 'gone', data: null };
      if (result?.code === 404) return { status: 'notFound', data: null };
    } catch (error: any) {
      const status = getPublicMusicDetailErrorStatus(error);
      if (status === 410) return { status: 'gone', data: null };
      if (status === 404) return { status: 'notFound', data: null };
    }
    return { status: 'unavailable', data: null };
  }
);

const unavailableResponseStatus = computed(() => {
  if (ssrMusicResult.value?.status === 'gone') return 410;
  if (ssrMusicResult.value?.status === 'notFound') return 404;
  return null;
});

if (import.meta.server && unavailableResponseStatus.value) {
  const status = unavailableResponseStatus.value;
  setResponseStatus(status, status === 410 ? 'Gone' : 'Not Found');
  useResponseHeader('X-Robots-Tag').value = 'noindex, nofollow, noarchive';
}

// Public SSR data initializes the same refs used by reactive head metadata.
if (ssrMusicResult.value?.status === 'available' && ssrMusicResult.value.data) {
  music.value = convertToViewModel(ssrMusicResult.value.data);
  loadedSongTarget = songTargetKey.value;
  isLoading.value = false;
} else if (!taskId.value && ssrMusicResult.value?.status !== 'unavailable') {
  isLoading.value = false;
}

useSeoMeta({
  robots: () => unavailableResponseStatus.value ? 'noindex,nofollow,noarchive' : 'index,follow',
});

useHead(() => isUnavailable.value ? {
  title: t('music.unavailableMetaTitle'),
  meta: [
    {
      name: 'description',
      content: t('music.unavailableMetaDescription'),
    },
    { property: 'og:title', content: t('music.unavailableMetaTitle') },
    {
      property: 'og:description',
      content: t('music.unavailableMetaDescription'),
    },
  ],
} : {});

// 加载音乐数据（仅用于 taskId 情况，或 SSR 未成功时的客户端兜底）
const syncTaskCreatorName = () => {
  if (!taskId.value || !music.value || taskHasDisplayName.value || !loadedTaskOwnerId.value || loadedTaskOwnerId.value !== userAccount.value?.id) return;
  const name = user.value?.fullName || user.value?.username;
  if (name && name !== music.value.display_name) music.value = { ...music.value, display_name: name };
};
watch([() => userAccount.value?.id, () => user.value?.fullName, () => user.value?.username], syncTaskCreatorName);
const loadMusicData = async () => {
  if (!songPageActive.value || route.path !== readingPath) return;
  if (isPrivateSong.value && !canReadPrivateSong.value) {
    isLoading.value = !isAuthReady.value || isAuthenticated.value;
    return;
  }
  const request = ++musicDataRequest;
  musicDataController?.abort();
  const controller = new AbortController();
  musicDataController = controller;
  const targetMusicId = musicId.value?.toString() || '';
  const targetTaskId = taskId.value;
  const identity = targetTaskId ? songSessionIdentity.value : '';
  const stillCurrent = () => request === musicDataRequest && !controller.signal.aborted
    && songPageActive.value && route.path === readingPath
    && targetMusicId === (musicId.value?.toString() || '') && targetTaskId === taskId.value
    && (!targetTaskId || (canReadPrivateSong.value && identity === songSessionIdentity.value));
  isLoading.value = true;
  musicLoadError.value = '';
  taskVersions.value = [];
  try {
    // 如果存在taskId，则通过getMusicTaskResult获取任务
    if (targetTaskId) {
      const token = await fetchAndStoreToken();
      if (!stillCurrent()) return;
      if (!token) throw new Error('Song session unavailable');
      const taskResult = await musicService.getMusicTaskResult({ task_id: targetTaskId }, undefined, {
        signal: controller.signal, musicmintInlineError: true,
      });
      if (!stillCurrent()) return;
      if (taskResult?.code === 404 || taskResult?.code === 410) { music.value = null; return; }
      if (taskResult?.code !== 200) throw new Error('Music unavailable');

      // 确保music_data是数组
      const musicDataArray = Array.isArray(taskResult?.data?.music_data)
        ? taskResult.data.music_data
        : (taskResult?.data?.music_data ? [taskResult.data.music_data] : []);

      if (musicDataArray.length > 0) {
        // 找到对应musicId的音乐，如果没指定则取第一个
        const targetMusic = targetMusicId
          ? musicDataArray.find((item: any) => item.id === targetMusicId)
          : musicDataArray[0];

        if (targetMusic) {
          taskVersions.value = musicDataArray.map((item: any, position: number) => ({
            id: item.id || '', duration: item.metadata?.duration || 0, position,
            ...(item.image_url ? { image_url: item.image_url } : {}),
          })).filter((version: { id: string }) => Boolean(version.id));
          loadedPrivateIdentity = identity;
          loadedSongTarget = songTargetKey.value;
          loadedTaskOwnerId.value = taskResult.data.user_id || '';
          taskHasDisplayName.value = Boolean(targetMusic.display_name);
          const ownerName = taskResult.data.user_id === userAccount.value?.id
            ? (user.value?.fullName || user.value?.username || '') : '';
          music.value = convertToViewModel({ ...targetMusic, display_name: targetMusic.display_name || ownerName });
          isLoading.value = false;
          return;
        }
      }
      music.value = null;
    } else if (targetMusicId && !music.value) {
      // SSR 未成功时的客户端兜底
      const musicData = await musicService.getPublicMusicDetail(targetMusicId, {
        signal: controller.signal, musicmintInlineError: true,
      });
      if (!stillCurrent()) return;
      if (musicData?.code === 200 && musicData.data) {
        music.value = convertToViewModel(musicData.data);
        loadedSongTarget = songTargetKey.value;
        loadedPrivateIdentity = '';
      } else if (musicData?.code === 404 || musicData?.code === 410 || musicData?.code === 200 && !musicData.data) {
        music.value = null;
      } else {
        throw new Error('Music unavailable');
      }
    }
  } catch (error) {
    if (stillCurrent()) {
      music.value = null;
      const status = getPublicMusicDetailErrorStatus(error);
      if (status !== 404 && status !== 410) musicLoadError.value = t('common.load_failed');
    }
  } finally {
    if (stillCurrent()) isLoading.value = false;
  }
};

const invalidateSongRead = () => {
  ++musicDataRequest;
  musicDataController?.abort();
};
const resetSongSession = () => {
  cancelSongDownload();
  downloadError.value = '';
  downloadNeedsMembership.value = false;
  if (!isPrivateSong.value && !loadedPrivateIdentity) return;
  invalidateSongRead();
  music.value = null;
  taskVersions.value = [];
  loadedTaskOwnerId.value = '';
  taskHasDisplayName.value = false;
  loadedPrivateIdentity = '';
  loadedSongTarget = '';
  musicLoadError.value = '';
  closeSongOverlays();
  isLoading.value = !isAuthReady.value || isAuthenticated.value;
  if (songHasMounted && songPageActive.value && route.path === readingPath && canReadPrivateSong.value) void loadMusicData();
};

// 格式化时间
const formatDuration = (seconds: number | undefined) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 格式化日期
const formatDate = (date: string) => {
  const value = new Date(date);
  return Number.isNaN(value.getTime()) ? date : value.toLocaleDateString(locale.value, { month: 'short', year: 'numeric' });
};

// The shell's player owns the only audio element, including lyric seeking.
const playMusic = () => {
  if (!music.value) return;
  if (isCurrentTrack.value) player.toggle();
  else player.play({ ...music.value, lyrics: music.value.description }, { surface: 'song_page', requestId: pageRequestId.value });
};
const seekLine = (line: number) => {
  if (!music.value || !lyricsLineCount.value || !songDuration.value) return;
  const seconds = timedLines.value[line]?.start_ms == null ? null : timedLines.value[line].start_ms / 1000;
  if (seconds === null) return;
  if (!isCurrentTrack.value || isAudioLoading.value) {
    pendingSeek.value = { id: music.value.id, seconds };
    if (!isCurrentTrack.value) playMusic();
  } else {
    player.seek(seconds);
    if (!player.playing && !player.loading) player.toggle();
  }
};
watch([() => player.phase, () => player.duration], () => {
  const seek = pendingSeek.value;
  if (!seek || player.currentTrack?.id !== seek.id) return;
  if (player.phase === 'error' || player.phase === 'paused') pendingSeek.value = null;
  else if (player.playing && player.duration > 0) { player.seek(seek.seconds); pendingSeek.value = null; }
});
const closeSongOverlays = () => {
  shareIntent.finish();
  giftOpen.value = false;
  lyricsOpen.value = menuOpen.value = shareOpen.value = false;
  pendingSeek.value = null;
  ++shareRequest;
  shareBusy.value = false;
  shareUrl.value = shareError.value = '';
};
watch([() => music.value?.id, songSessionIdentity, locale], closeSongOverlays);
watch(shareOpen, value => { if (!value) { shareRequest++; shareIntent.finish(); shareBusy.value=false; shareUrl.value=''; shareError.value=''; } });

const handleShare = async () => {
  if (!music.value || shareBusy.value) return;
  const song = music.value;
  const request = ++shareRequest;
  const identity = songSessionIdentity.value;
  shareOpen.value = true;
  shareError.value = '';
  shareUrl.value = '';
  if (!isPrivateSong.value) { shareUrl.value = `${siteUrl}/music/song/${song.id}`; return; }
  shareBusy.value = true;
  try {
    const result = await shareIntent.generate({ id: song.id });
    if (request !== shareRequest || identity !== songSessionIdentity.value || music.value?.id !== song.id) return;
    if (result.code !== 200 || !result.data?.token) throw new Error('Share unavailable');
    shareUrl.value = `${siteUrl}/share/${generateFullSlug(result.data.token, song.title)}`;
  } catch (error) {
    if (request === shareRequest) shareError.value = t(shareIntent.errorKey(error));
  } finally { if (request === shareRequest) shareBusy.value = false; }
};
const copyShareLink = async () => {
  if (!shareUrl.value) return;
  const request=shareRequest, url=shareUrl.value;
  try { await navigator.clipboard.writeText(url); if(request===shareRequest&&shareOpen.value&&shareUrl.value===url) toast.success(t('ux.copied')); }
  catch { if(request===shareRequest&&shareOpen.value) shareError.value = t('ux.shareCopyFailed'); }
};

// 监听播放状态变化
onMounted(() => {
  songHasMounted = true;
  songPageActive.value = true;
  pageRequestId.value = createGrowthEventId();
  try {
    const intent = JSON.parse(sessionStorage.getItem('musicmint_pending_song_like') || 'null');
    if (intent?.id === music.value?.id && Date.now() - intent.at < 600000) pendingLike.value = intent.id;
  } catch { /* Storage is optional. */ }
  if (isAuthenticated.value) void syncSongLikeIdentity();
  if (music.value) player.openSong(music.value);
  const visibleCover = [desktopCoverImage.value].find(image => image && image.offsetWidth > 0);
  if (music.value?.image_url && visibleCover?.complete && !visibleCover.naturalWidth) coverError.value = true;
  // 仅在 taskId 存在（私有任务数据）或 SSR 未成功获取到数据时才在客户端拉取
  if ((isPrivateSong.value && canReadPrivateSong.value) || (!isPrivateSong.value && isLoading.value && !music.value)) {
    void loadMusicData();
  }

  if (music.value) {
    trackEvent('song_detail_view', { source: 'song_detail', page_path: route.path, song_id: musicId.value as string, tags: music.value?.tags });
  } else {
    trackEvent('song_detail_unavailable_view', { source: 'song_detail', page_path: route.path, song_id: musicId.value as string });
  }

});

onActivated(() => {
  if (songPageActive.value) return;
  songPageActive.value = true;
  if (music.value) player.openSong(music.value);
  if (music.value && loadedSongTarget !== songTargetKey.value) {
    music.value = null;
    taskVersions.value = [];
    loadedTaskOwnerId.value = '';
    taskHasDisplayName.value = false;
    loadedPrivateIdentity = '';
  }
  if ((isPrivateSong.value && canReadPrivateSong.value && (!music.value || loadedPrivateIdentity !== songSessionIdentity.value))
    || (!isPrivateSong.value && !music.value && (musicLoadError.value || loadedSongTarget !== songTargetKey.value))) void loadMusicData();
});
onDeactivated(() => {
  closeSongOverlays();
  songPageActive.value = false;
  invalidateSongRead();
  cancelSongDownload();
});

// 组件卸载时清理
onUnmounted(() => {
  songPageActive.value = false;
  invalidateSongRead();
  cancelSongDownload();
  closeSongOverlays();
});

// Register head effects during setup; asynchronous task results only update refs.
const songSeoTags = computed(() => music.value ? songSeoTagText(music.value.tags, t('music.unknown')) : '');
const songSeoDescription = computed(() => music.value ? t('meta.musicSongId.description', { title: music.value.title, tags: songSeoTags.value }) : undefined);
const songSeoOgTitle = computed(() => music.value ? t('meta.musicSongId.ogTitle', { title: music.value.title }) : undefined);
const songSeoOgDescription = computed(() => music.value ? t('meta.musicSongId.ogDescription', { description: music.value.description?.substring(0, 150) || t('music.onlineListen') }) : undefined);
useSeoMeta({
  title: () => music.value ? t('meta.musicSongId.title', { title: music.value.title }) : undefined,
  description: () => songSeoDescription.value,
  keywords: () => music.value ? t('meta.musicSongId.keywords', { title: music.value.title, tags: songSeoTags.value }) : undefined,
  ogTitle: () => songSeoOgTitle.value,
  ogDescription: () => songSeoOgDescription.value,
  ogType: 'music.song',
  ogUrl: () => music.value ? `${siteUrl}/music/song/${music.value.id}` : undefined,
  ogImage: () => music.value?.image_url || `${siteUrl}/images/ogimage.png`,
  ogLocale: () => localeMappings.shortToFull[locale.value as keyof typeof localeMappings.shortToFull] || 'en_US',
  ogSiteName: 'MusicMint',
  twitterCard: 'summary_large_image',
  twitterTitle: () => songSeoOgTitle.value,
  twitterDescription: () => songSeoOgDescription.value,
  twitterImage: () => music.value?.image_url || `${siteUrl}/images/ogimage.png`,
});
useHead(() => !music.value ? { htmlAttrs: { lang: locale.value } } : {
  htmlAttrs: { lang: locale.value },
  link: [{ key: 'canonical', rel: 'canonical', href: `${siteUrl}${route.path}` }],
  script: [{
    key: 'song-schema', type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org', '@type': 'MusicComposition',
      name: music.value.title, composer: { '@type': 'Person', name: music.value.display_name },
      datePublished: music.value.releaseDate, description: music.value.description,
      genre: songSeoTags.value.split(','), image: music.value.image_url,
      url: `${siteUrl}/music/song/${music.value.id}`,
      potentialAction: { '@type': 'ListenAction', target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/music/song/${music.value.id}` } },
      mainEntity: {
        '@type': 'MusicRecording', name: music.value.title,
        byArtist: { '@type': 'Person', name: music.value.display_name },
        duration: music.value.duration ? `PT${Math.floor(music.value.duration / 60)}M${Math.floor(music.value.duration % 60)}S` : undefined,
        isrcCode: music.value.id,
        recordingOf: { '@type': 'MusicComposition', name: music.value.title, description: music.value.description },
      },
    }),
  }],
});

const isDownloading = ref(false);
const downloadError = ref('');
const downloadNeedsMembership = ref(false);
let songDownloadRequest = 0;
let songDownloadController: AbortController | undefined;
const cancelSongDownload = () => {
  ++songDownloadRequest;
  songDownloadController?.abort();
  isDownloading.value = false;
};
const handleDownloadMusic = async (song: MusicViewModel) => {
  if (isDownloading.value || !songPageActive.value || route.path !== readingPath || song.id !== music.value?.id) return;
  downloadError.value = '';
  downloadNeedsMembership.value = false;
  if (!user.value) { toast.error(t('user.loginToDownloadMusic')); await handleSignIn(); return; }
  if (!userAccount.value) { downloadError.value = t('common.load_failed'); return; }
  if (userAccount.value.vip_level <= 0) {
    downloadNeedsMembership.value = true;
    downloadError.value = t('user.notEnoughVip');
    trackEvent('download_blocked_membership', { surface: 'song_detail', music_id: song.id });
    openPaywall({ trigger: 'download', surface: 'song_detail' });
    return;
  }
  if (!song.audio_url) { downloadError.value = t('music.downloadFailed'); return; }
  const request = ++songDownloadRequest;
  const identity = songSessionIdentity.value;
  const controller = new AbortController();
  songDownloadController = controller;
  const stillCurrent = () => request === songDownloadRequest && !controller.signal.aborted
    && songPageActive.value && route.path === readingPath
    && identity === songSessionIdentity.value && music.value?.id === song.id;
  isDownloading.value = true;
  toast.info(t('music.downloadStarted'));
  try {
    const response = await fetch(song.audio_url, { signal: controller.signal });
    if (!stillCurrent()) return;
    if (!response.ok) throw new Error('Audio download unavailable');
    const blob = await response.blob();
    if (!stillCurrent()) return;
    if (!blob.size) throw new Error('Empty audio');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${song.title || 'music'}.mp3`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(t('music.downloadSuccess'));
  } catch {
    if (!stillCurrent()) return;
    downloadError.value = t('music.downloadFailed');
    toast.error(downloadError.value);
  } finally { if (request === songDownloadRequest) isDownloading.value = false; }
};
const retryDownloadMusic = async (song: MusicViewModel) => {
  if (isDownloading.value || !songPageActive.value || route.path !== readingPath || song.id !== music.value?.id) return;
  const request = ++songDownloadRequest;
  const identity = songSessionIdentity.value;
  const stillCurrent = () => request === songDownloadRequest && songPageActive.value && route.path === readingPath
    && identity === songSessionIdentity.value && music.value?.id === song.id;
  isDownloading.value = true;
  try { await refreshUserAccount(); }
  catch { if (stillCurrent()) downloadError.value = t('common.load_failed'); return; }
  finally { if (request === songDownloadRequest) isDownloading.value = false; }
  if (!stillCurrent()) return;
  await handleDownloadMusic(song);
};

// Tag-to-discover page matching
const tagToDiscoverMap: Array<{ keywords: string[]; slug: string }> = [
  { keywords: ['rap', 'hip hop', 'hip-hop'], slug: 'ai-rap-song-generator' },
  { keywords: ['pop'], slug: 'ai-pop-song-generator' },
  { keywords: ['country'], slug: 'ai-country-song-generator' },
  { keywords: ['rock'], slug: 'ai-rock-song-generator' },
  { keywords: ['anime', 'jpop', 'j-pop'], slug: 'ai-anime-song-generator' },
  { keywords: ['lofi', 'lo-fi', 'chill'], slug: 'ai-lofi-music-generator' },
  { keywords: ['jazz'], slug: 'ai-jazz-music-generator' },
  { keywords: ['edm', 'electronic', 'dance'], slug: 'ai-edm-generator' },
  { keywords: ['birthday'], slug: 'ai-birthday-song-generator' },
  { keywords: ['love', 'romantic'], slug: 'ai-love-song-generator' },
  { keywords: ['funny', 'comedy'], slug: 'ai-funny-song-generator' },
];

const relatedDiscoverPages = computed(() => {
  if (!music.value?.tags) return [];
  const tagsLower = music.value.tags.toLowerCase();
  const matched: SeoLandingPage[] = [];

  for (const mapping of tagToDiscoverMap) {
    if (mapping.keywords.some(kw => tagsLower.includes(kw))) {
      const page = seoLandingPages.find(p => p.slug === mapping.slug);
      if (page) matched.push(page);
    }
    if (matched.length >= 4) break;
  }
  return matched;
});

const handleCreateSimilar = () => {
  if (!music.value || !canRemix.value) return;
  trackEvent('song_detail_create_similar_click', { source: 'song_detail', page_path: route.path, song_id: music.value.id, tags: music.value.tags });
  saveMusicData({
    prompt: music.value.description,
    tags: music.value.tags,
    title: music.value.title,
    mode: 'normal',
    source: 'song_detail',
    sourceSongId: music.value.id,
  }, '/create');
  void router.push(similarTo.value);
};

watch(songSessionIdentity, resetSongSession, { immediate: process.client, flush: 'sync' });
watch([musicId, taskId], () => {
  if (!songPageActive.value || route.path !== readingPath) return;
  invalidateSongRead();
  cancelSongDownload();
  music.value = null;
  taskVersions.value = [];
  loadedTaskOwnerId.value = '';
  taskHasDisplayName.value = false;
  loadedPrivateIdentity = '';
  loadedSongTarget = '';
  downloadError.value = '';
  downloadNeedsMembership.value = false;
  void loadMusicData();
}, { flush: 'sync' });
</script>

<template>
  <div ref="songRoot" class="mm-root t24b-page music-detail-page" :style="{ '--live-h': music?.cover_hue ?? 152 }">
    <section class="s-hero">
      <div class="wrap">
        <MmButton variant="ghost" size="sm" class="song-back" @click="router.back()"><MmIcon name="left" :size="18" />{{ t('music.back') }}</MmButton>
        <div v-if="isLoading" role="status" class="song-load-state">
          <MmIcon name="disc" /><h1 class="t-h2">{{ t('ux.loadingSong') }}</h1>
        </div>
        <div v-else-if="needsSongSignIn" class="song-load-state">
          <h1 ref="songHeading" tabindex="-1" class="t-h2">{{ t('ux.signInForSong') }}</h1>
          <MmButton @click="handleSignIn">{{ t('user.login') }}</MmButton>
        </div>
        <div v-else-if="musicLoadError" class="song-load-state">
          <h1 ref="songHeading" tabindex="-1" class="t-h2">{{ t('ux.songLoadFailedTitle') }}</h1>
          <p role="alert">{{ t('ux.songLoadFailedDescription') }}</p>
          <MmButton variant="secondary" @click="loadMusicData">{{ t('ux.retry') }}</MmButton>
        </div>
        <div v-else-if="music" class="s-grid">
          <div class="s-art" :class="{ 'is-playing': isPlaying }">
            <Vinyl :src="musicCoverUrl(music.image_url, 160)" :hue="music.cover_hue" :spinning="isPlaying" :paused="!isPlaying" />
            <div class="cover grain song-cover">
              <img ref="desktopCoverImage" :src="coverSrc" :srcset="coverSrcset" :sizes="coverSizes" :alt="music.title"
                width="380" height="380" fetchpriority="high" loading="eager" decoding="async" @error="coverError = true" />
            </div>
            <MmSegmented v-if="taskVersions.length > 1" class="side-switch" :model-value="music.id" :options="sideOptions" :label="t('mm.song.chooseSide')" semantics="navigation" />
          </div>
          <header class="s-meta">
            <p class="t-eyebrow">{{ t('mm.shell.song') }}<template v-if="currentVersion"> · {{ t('mm.song.side', { side: String.fromCharCode(65 + currentVersion.position) }) }}</template></p>
            <h1 id="song-title" ref="songHeading" class="t-display s-title" tabindex="-1">{{ music.title }}</h1>
            <div class="s-by">
              <CreatorCredit :creator="music.creator?.handle && music.creator.handle === music.creator.display_name ? { ...music.creator, display_name: '@' + music.creator.display_name } : music.creator" /><span v-if="music.releaseDate">·</span>
              <time v-if="music.releaseDate" :datetime="music.releaseDate" :aria-label="t('music.releaseDate')">{{ formatDate(music.releaseDate) }}</time>
              <span>·</span><span class="t-mono">{{ formatDuration(music.duration) }}</span>
            </div>
            <ul v-if="songStyleTags.length" class="cluster song-style-tags" :aria-label="t('ux.songStyleDetails')">
              <li v-for="tag in songStyleTags" :key="tag" class="chip sm">{{ tag }}</li>
            </ul>
            <div v-if="music.playCount !== undefined" class="s-stats">
              <span v-if="music.playCount !== undefined"><MmIcon name="play" :size="12" />{{ t('mm.plays', { count: compactCount(music.playCount) }) }}</span>
              <span><MmIcon name="heart" :size="13" />{{ compactCount(likeState.like_count) }}</span>
            </div>
            <div class="s-actions">
              <PlayButton :title="music.title" :state="playbackState" size="lg" cancelable :label="isAudioLoading ? `${t('ux.cancelPlayback')} ${music.title}` : undefined" @play="playMusic" />
              <button type="button" class="icon-btn like" :aria-pressed="likeState.liked" :aria-label="t(likeState.liked ? 'mm.song.unlikeTrack' : 'mm.song.likeTrack', { title: music.title, count: likeState.like_count })" :disabled="likeBusy" @click="toggleLike()"><MmIcon name="heart" :size="22" /></button>
              <button type="button" class="btn btn-secondary song-share" @click="handleShare"><MmIcon name="share" :size="18" />{{ t('albumCoverGenerator.preview.share') }}</button>
              <NuxtLink v-if="canRemix" :to="similarTo" class="btn btn-secondary song-similar" @click.prevent="handleCreateSimilar"><MmIcon name="wand" :size="18" />{{ t('t24b.createSimilar') }}</NuxtLink>
              <MmButton v-if="isOwner" class="song-owner-action" variant="secondary" :loading="isDownloading" :aria-label="`${t('music.download')} ${music.title}`" @click="handleDownloadMusic(music)"><MmIcon name="download" :size="18" />{{ t('music.download') }}</MmButton>
              <NuxtLink v-if="isOwner && taskId" :to="{ path: '/copyright', query: { task_id: taskId, music_id: music.id } }" class="btn btn-secondary song-owner-action" :aria-label="`${t('ux.certificate')} · ${music.title}`"><MmIcon name="award" :size="18" />{{ t('ux.certificate') }}</NuxtLink>
              <MmMenu v-model="menuOpen" :title="music.title" :label="t('mm.moreFor', { title: music.title })" :items="songMenuItems" @select="selectSongMenu">
                <template #trigger="{ toggle, expanded, id }"><button type="button" class="icon-btn" :aria-label="t('mm.moreFor', { title: music.title })" aria-haspopup="menu" :aria-expanded="expanded" :aria-controls="id" @click="toggle"><MmIcon name="more" /></button></template>
              </MmMenu>
            </div>
            <p v-if="likeError" class="song-error" role="alert">{{ likeError }}</p>
            <p v-if="isCurrentTrack && player.playbackError" role="alert" class="song-error">{{ t('ux.playbackFailed') }}</p>
            <div v-if="downloadError" role="alert" class="song-download-feedback">
              <p>{{ downloadError }}</p>
              <NuxtLink v-if="downloadNeedsMembership" :to="pricingPath('song_detail_download')" class="btn btn-secondary">{{ t('nav.pricing') }}</NuxtLink>
              <MmButton v-else variant="secondary" :disabled="isDownloading" @click="retryDownloadMusic(music)">{{ t('ux.retry') }}</MmButton>
            </div>
          </header>
        </div>
        <div v-else class="song-load-state">
          <h1 ref="songHeading" tabindex="-1" class="t-h2">{{ t('music.unavailableTitle') }}</h1>
          <p>{{ t('music.unavailableDescription') }}</p>
          <div class="song-unavailable-actions">
            <MmButton @click="router.push('/music/global-trending')">{{ t('music.exploreTrending') }}</MmButton>
            <MmButton variant="secondary" @click="router.push('/create')">{{ t('music.createOwnSong') }}</MmButton>
          </div>
        </div>
      </div>
    </section>

    <section v-if="!isLoading && !needsSongSignIn && !musicLoadError && music" class="wrap s-body" :class="{ 'no-lyrics': !lyricsSections.length }">
      <section v-if="lyricsSections.length" id="lyrics" class="card song-lyrics" aria-labelledby="song-lyrics-title">
        <div class="lyr-head">
          <h2 id="song-lyrics-title" class="t-h2">{{ t('music.lyrics') }}</h2>
          <div v-if="lyricsSections.length" class="lyr-tools cluster">
            <MmSegmented v-model="lyricsMode" :label="t('music.lyrics')" :options="[{ value: 'sync', label: t('mm.song.singAlong') }, { value: 'text', label: t('mm.song.fullText') }]" />
            <button type="button" class="icon-btn sm" :aria-label="t('mm.song.fullscreenLyrics', { title: music.title })" @click="lyricsOpen = true"><MmIcon name="expand" :size="20" /></button>
          </div>
        </div>
        <LyricsSingAlong v-if="lyricsSections.length" :sections="lyricsSections" :active-line="activeLine" :estimated="estimated" :full-text="lyricsMode === 'text'" :seekable="songDuration > 0" @seek="seekLine" />
        <p v-else class="song-empty-lyrics">{{ t('music.noLyrics') }}</p>
      </section>
      <aside class="song-context stack">
        <section class="card notes" aria-labelledby="song-notes-title">
          <h2 id="song-notes-title" class="t-eyebrow">{{ t('mm.song.linerNotes') }}</h2>
          <p v-if="music.linerPrompt" class="prompt">“{{ music.linerPrompt }}”</p>
          <dl>
            <template v-if="music.hasStyleTags"><dt>{{ t('mm.song.style') }}</dt><dd>{{ music.tags }}</dd></template>
            <template v-if="music.mood"><dt>{{ t('mm.song.mood') }}</dt><dd>{{ music.mood }}</dd></template>
            <template v-if="music.voice"><dt>{{ t('mm.song.voice') }}</dt><dd>{{ music.voice }}</dd></template>
            <dt>{{ t('mm.song.length') }}</dt><dd class="t-mono">{{ formatDuration(music.duration) }}</dd>
          </dl>
        </section>
        <section v-if="canRemix" class="card remix-cta" aria-labelledby="song-remix-title">
          <p class="t-eyebrow">{{ t('mm.song.likeThisVibe') }}</p>
          <i18n-t id="song-remix-title" keypath="t24b.makeVersion" tag="h2"><template #version><span class="t-serif">{{ t('t24b.version') }}</span></template></i18n-t>
          <p class="muted">{{ t('t24b.remixHint') }}</p>
          <NuxtLink :to="similarTo" class="btn btn-primary" @click.prevent="handleCreateSimilar"><MmIcon name="sparkle" :size="18" />{{ t('t24b.createSimilar') }}</NuxtLink>
        </section>
        <SimilarSongs :song-id="music.id" />
      </aside>
    </section>
    <section v-if="music && !isLoading && !needsSongSignIn && !musicLoadError" class="wrap song-recommendations">
        <section v-if="relatedDiscoverPages.length" class="card song-related" aria-labelledby="song-related-title">
          <h2 id="song-related-title" class="t-h3">{{ t('ux.songRelatedGenerators') }}</h2>
          <NuxtLink v-for="page in relatedDiscoverPages" :key="page.slug" :to="`/discover/${page.slug}`"><span>{{ page.h1 }}</span><MmIcon name="arrowUR" :size="18" /></NuxtLink>
        </section>
    </section>
    <LyricsTakeover v-if="music && lyricsSections.length" v-model="lyricsOpen" :title="music.title" :creator="music.display_name" :hue="music.cover_hue" :sections="lyricsSections" :active-line="activeLine" :estimated="estimated" :seekable="songDuration > 0" :state="playbackState" @seek="seekLine" @play="playMusic" />
    <GiftShareDialog v-if="canGift && music && giftOpen" v-model="giftOpen" :song="music" surface="song_page" />
    <MmShareDialog v-model="shareOpen" :song="music" :url="shareUrl" :pending="shareBusy" :error="shareError" @copy="copyShareLink" @retry="handleShare" />
  </div>
</template>

<style scoped>

  .s-hero { position: relative; z-index: 1; padding: 20px 0 32px; }
  .s-hero::before { content: ""; position: absolute; inset: calc(var(--nav-h) * -1) 0 0; z-index: -1; background: var(--song-hero-gradient); transition: background var(--d-story); }
  .s-grid { display: grid; gap: 24px; align-items: end; }
  @media (min-width: 900px) { .s-grid { grid-template-columns: 340px minmax(0, 1fr); gap: 44px; } .s-hero { padding: 40px 0 44px; } }
  @media (min-width: 1280px) { .s-grid { grid-template-columns: 380px minmax(0, 1fr); } }
  .s-art { position: relative; width: min(72vw, 100%); justify-self: center; }
  @media (min-width: 900px) { .s-art { width: 100%; } }
  .s-art :deep(.vinyl) { inset: 3%; transition: transform var(--d-slow) var(--ease-out); }
  /* The shared Vinyl rotates an inner layer; keep the disc inside the 44px title gutter (SO01). */
  .s-art.is-playing :deep(.vinyl) { transform: translateX(10%); }
  .s-art .cover { z-index: 2; border-radius: var(--s-4); box-shadow: var(--e-3); view-transition-name: hero-cover; }
  .s-art .side-switch { position: absolute; z-index: 3; left: 12px; top: 12px; }
  .s-title { font-size: clamp(34px, 5.4vw, 76px); margin: 10px 0 14px; overflow-wrap: anywhere; }
  .s-by { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; color: var(--text-2); font-size: 15px; }
  .s-by .avatar { width: 28px; height: 28px; font-size: 11px; }
  .s-by a { color: var(--text); font-weight: 600; }
  .s-stats { display: flex; gap: 18px; margin-top: 12px; color: var(--text-2); font: 500 13px/1 var(--font-mono); }
  .s-stats span { display: inline-flex; gap: 6px; align-items: center; }
  .s-actions { display: flex; align-items: center; gap: 8px; margin-top: 22px; flex-wrap: wrap; }
  .s-actions .play { --size: 60px; }
  @media (max-width: 899px) { .s-meta { text-align: left; } }

  .s-body { display: grid; gap: 24px; padding-top: 8px; }
  @media (min-width: 1024px) { .s-body { grid-template-columns: minmax(0, 1fr) 380px; gap: 40px; align-items: start; } }

  .card { border-radius: var(--r-xl); background: var(--surface); border: 1px solid var(--line); padding: 20px; }
  .notes dl { display: grid; grid-template-columns: 92px 1fr; gap: 12px 14px; margin: 0; font-size: 14px; }
  .notes dt { color: var(--text-3); font: 500 12px/1.6 var(--font-mono); text-transform: uppercase; letter-spacing: .08em; }
  .notes dd { margin: 0; color: var(--text); }
  .notes .prompt { font: 400 21px/1.3 var(--font-serif); font-style: italic; color: var(--text); margin: 4px 0 16px; }
  .remix-cta { position: relative; overflow: hidden; background: var(--song-remix-gradient); }


:global(.shell-content:has(> .music-detail-page)) { padding: 0; }
.music-detail-page { min-width: 0; }
.song-back { margin: 0 0 12px -10px; }
.song-cover img { width: 100%; height: 100%; object-fit: cover; }
.s-art { isolation: isolate; }
.s-art :deep(.vinyl) { z-index: -1; }
.s-meta { position: relative; z-index: 2; min-width: 0; }
.s-meta > .t-eyebrow { margin: 0; color: var(--text-2); }
.s-by :deep(.creator-credit) { gap: 10px; font-size: 15px; }
.s-by :deep(.creator-avatar) { width: 28px; height: 28px; font: 700 11px/1 var(--font-display); background: conic-gradient(from 200deg, var(--pop-pink), var(--pop-sun), var(--accent), var(--pop-sky), var(--pop-pink)); color: var(--accent-ink); }
.song-style-tags { margin: 14px 0 0; padding: 0; list-style: none; }
.song-style-tags li { font-size: 12.5px; }
.s-actions :deep(.play) { --size: 60px; }
.song-lyrics { min-width: 0; padding-bottom: 8px; }
.lyr-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; }
.lyr-head h2 { font-size: 22px; }
.lyr-tools { --gap: 6px; }
.song-context { --gap: 20px; min-width: 0; }
.remix-cta > .t-eyebrow { color: var(--text-2); margin: 0; }
.remix-cta h2 { font: 700 22px/1.15 var(--font-display); letter-spacing: -.03em; margin: 8px 0 6px; }
.remix-cta > .muted { font-size: 14px; margin: 0 0 14px; }
.song-recommendations { margin-top: 24px; }
.song-related h2 { margin-bottom: 12px; }
.song-related a { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 44px; padding-block: 8px; color: var(--text-2); font-size: 14px; }
.song-load-state { min-height: 360px; display: grid; align-content: center; justify-items: start; gap: 20px; }
.song-load-state p { color: var(--text-2); }
.song-unavailable-actions { display: flex; flex-wrap: wrap; gap: 12px; }
.song-error, .song-download-feedback { color: var(--danger); margin-top: 12px; }
.s-body.no-lyrics { grid-template-columns: 1fr; }
.side-switch { max-width: calc(100% - 24px); }

</style>
